import { DownloadOutlined } from "@ant-design/icons";
import { Button, DatePicker, Empty, Flex, Form, Select } from "antd";
import type { Dayjs } from "dayjs";
import { useEffect, useMemo } from "react";
import { connect, type DispatchProp } from "react-redux";
import { Card } from "../../common/components/Card.tsx";
import { PageTitleContainer } from "../../common/components/PageTitle.tsx";
import { getUserDisplayName } from "../../common/utils/userDisplayName.ts";
import {
  reportsActions,
  type TechniciansReportFilters,
} from "../../features/reports/reportsSlice.ts";
import ConnectedAppLayout from "../AppLayout.tsx";
import type { RootState } from "../store.ts";
import styles from "./TechniciansReportPage.module.css";
import { TechniciansReportTable } from "./TechniciansReportTable.tsx";

type FilterFormValues = {
  technicians?: number[];
  period?: [Dayjs | null, Dayjs | null] | null;
};

const API_DATE_FORMAT = "YYYY-MM-DD";

const toReportFilters = (values: FilterFormValues): TechniciansReportFilters => ({
  technicians: values.technicians ?? [],
  dateFrom: values.period?.[0]?.format(API_DATE_FORMAT),
  dateTo: values.period?.[1]?.format(API_DATE_FORMAT),
});

type TechniciansReportPageProps = ReturnType<typeof withTechniciansReport> & DispatchProp;

export const TechniciansReportPage = (props: TechniciansReportPageProps) => {
  const { intl, dispatch, technicians, summary, summaryStatus, downloadStatus } = props;
  const [form] = Form.useForm<FilterFormValues>();

  useEffect(() => {
    dispatch(reportsActions.fetchTechniciansReportSummary(toReportFilters({})));
    return () => {
      dispatch(reportsActions.clearReports());
    };
  }, [dispatch]);

  const technicianOptions = useMemo(
    () =>
      (technicians ?? [])
        .map((technician) => ({
          value: technician.id,
          label: getUserDisplayName(technician),
        }))
        .sort((first, second) => first.label.localeCompare(second.label)),
    [technicians]
  );

  const summaryLoading = summaryStatus === "loading";

  return (
    <ConnectedAppLayout>
      <PageTitleContainer
        title={intl.formatMessage({ id: "technicians_report_title" })}
        subtitle={intl.formatMessage({ id: "technicians_report_subtitle" })}
        extra={
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            loading={downloadStatus === "loading"}
            disabled={summary === null || summaryLoading}
            onClick={() => dispatch(reportsActions.downloadTechniciansReport())}
          >
            {intl.formatMessage({ id: "technicians_report_download_excel" })}
          </Button>
        }
      >
        <Card>
          <Form
            form={form}
            layout="vertical"
            onValuesChange={(_, allValues) => {
              dispatch(
                reportsActions.fetchTechniciansReportSummary(toReportFilters(allValues))
              );
            }}
          >
            <Flex gap="middle" wrap align="end">
              <Form.Item
                name="technicians"
                label={intl.formatMessage({
                  id: "technicians_report_filter_technicians",
                })}
                className={styles.filterItem}
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  maxTagCount="responsive"
                  loading={technicians === null}
                  placeholder={intl.formatMessage({
                    id: "technicians_report_all_technicians",
                  })}
                  options={technicianOptions}
                />
              </Form.Item>
              <Form.Item
                name="period"
                label={intl.formatMessage({ id: "technicians_report_filter_period" })}
                className={styles.filterItem}
              >
                <DatePicker.RangePicker allowEmpty={[true, true]} />
              </Form.Item>
            </Flex>
          </Form>
        </Card>

        <Card>
          {summaryStatus === "failed" ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={intl.formatMessage({ id: "technicians_report_load_failed" })}
            />
          ) : (
            <TechniciansReportTable
              intl={intl}
              rows={summary?.technicians}
              loading={summaryLoading}
            />
          )}
        </Card>
      </PageTitleContainer>
    </ConnectedAppLayout>
  );
};

const withTechniciansReport = (state: RootState) => ({
  intl: state.intl.datetimeIntl,
  technicians: state.assignmentSchedule.technicians,
  summary: state.reports.techniciansReportSummary,
  summaryStatus: state.reports.techniciansReportSummaryStatus,
  downloadStatus: state.reports.techniciansReportDownloadStatus,
});

export const ConnectedTechniciansReportPage =
  connect(withTechniciansReport)(TechniciansReportPage);
export default ConnectedTechniciansReportPage;
